// Household item endpoints — the shared data layer for Lists (see README.md).
// Includes list-item pictures (name-keyed; see #299).

import { z } from 'zod';
import type {
  HouseholdItemFilters,
  CreateHouseholdItemPayload,
  UpdateHouseholdItemPayload,
} from '@/types/api';
import { householdItemResponseSchema, createHouseholdItemResponseSchema } from './schema';
import { itemPictureSchema } from '@/features/common/schema';
import { apiFetch, apiUpload, apiFetchBlob } from '@/lib/http';
import { nullableArray } from '@/lib/schemas/primitives';

export async function fetchHouseholdItems(
  filters?: HouseholdItemFilters
) {
  const params = new URLSearchParams();
  if (filters?.type) params.append('type', filters.type);
  if (filters?.state) params.append('state', filters.state);
  if (filters?.assignee !== undefined) params.append('assignee', String(filters.assignee));
  if (filters?.listId !== undefined) params.append('listId', String(filters.listId));

  const query = params.toString();
  return apiFetch(`/household-items${query ? `?${query}` : ''}`, {
    schema: z.object({ items: nullableArray(householdItemResponseSchema) }),
  });
}

export async function createHouseholdItem(
  data: CreateHouseholdItemPayload
) {
  return apiFetch('/household-items', {
    method: 'POST',
    body: JSON.stringify(data),
    schema: createHouseholdItemResponseSchema,
  });
}

export async function updateHouseholdItem(
  id: number,
  data: UpdateHouseholdItemPayload
) {
  return apiFetch(`/household-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    schema: householdItemResponseSchema,
  });
}

export async function deleteHouseholdItem(id: number) {
  return apiFetch(`/household-items/${id}`, {
    method: 'DELETE',
  });
}

export async function toggleHouseholdItem(id: number) {
  return apiFetch(`/household-items/${id}/toggle`, {
    method: 'PATCH',
    schema: householdItemResponseSchema,
  });
}

export async function reorderHouseholdItems(listId: number, itemIds: number[]) {
  return apiFetch('/household-items/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ listId, itemIds }),
  });
}

export async function clearCompletedHouseholdItems(
  listId: number
) {
  return apiFetch('/household-items/clear-completed', {
    method: 'PATCH',
    body: JSON.stringify({ listId }),
    schema: z.object({ clearedIds: nullableArray(z.number()) }),
  });
}

export async function restoreHouseholdItems(
  itemIds: number[]
) {
  return apiFetch('/household-items/restore', {
    method: 'PATCH',
    body: JSON.stringify({ itemIds }),
    schema: z.object({ restoredIds: nullableArray(z.number()) }),
  });
}

// List item picture (name-keyed; see #299)
export async function uploadListItemPicture(itemId: number, file: File) {
  return apiUpload(`/household-items/${itemId}/picture`, 'picture', file, { schema: itemPictureSchema });
}

export async function fetchListItemPicturePreview(itemId: number): Promise<Blob> {
  return apiFetchBlob(`/household-items/${itemId}/picture/preview`);
}

export async function deleteListItemPicture(itemId: number) {
  return apiFetch(`/household-items/${itemId}/picture`, { method: 'DELETE' });
}
